/**
 * @file Recent Modules Service
 * Tracks and retrieves the most recently used modules per user using IndexedDB.
 */

import { getNoSqlDb } from '../utility/no-sql-db';

/** Key used for storing recent modules in IndexedDB */
const RECENT_MODULES_KEY = 'recent-modules';

/** Maximum number of recent modules to track */
const MAX_RECENT_MODULES = 20;

/** Expiration time: 30 days in seconds */
const EXPIRY_SECONDS = 30 * 24 * 60 * 60;

/**
 * Represents a recently used module entry
 */
export interface RecentModuleEntry {
  /** The module ID (matches ResolvedModule.id) */
  moduleId: string;
  /** Timestamp when the module was last accessed */
  lastAccessed: number;
}

/**
 * Records that a user accessed a module.
 * Updates the lastAccessed timestamp if the module already exists,
 * or adds it as a new entry.
 *
 * @param moduleId - The ID of the module that was accessed
 * @param userId - The user's ID for data isolation
 */
export async function recordModuleUsage(
  moduleId: string,
  userId: string
): Promise<void> {
  if (!moduleId || !userId) return;

  const db = getNoSqlDb();
  const existing =
    (await db.getItem<RecentModuleEntry[]>(RECENT_MODULES_KEY, userId)) ?? [];

  // Remove existing entry for this module (if any)
  const filtered = existing.filter((e) => e.moduleId !== moduleId);

  // Add new entry at the front
  const updated: RecentModuleEntry[] = [
    { moduleId, lastAccessed: Date.now() },
    ...filtered,
  ].slice(0, MAX_RECENT_MODULES);

  await db.setUserItem(RECENT_MODULES_KEY, userId, updated, EXPIRY_SECONDS);
}

/**
 * Retrieves the most recently used modules for a user.
 *
 * @param userId - The user's ID
 * @param limit - Maximum number of modules to return (default: 5)
 * @returns Array of recent module entries, sorted by most recent first
 */
export async function getRecentModules(
  userId: string,
  limit: number = 5
): Promise<RecentModuleEntry[]> {
  if (!userId) return [];

  const db = getNoSqlDb();
  const entries =
    (await db.getItem<RecentModuleEntry[]>(RECENT_MODULES_KEY, userId)) ?? [];

  // Already sorted by most recent first (from recordModuleUsage), just slice
  return entries.slice(0, limit);
}

/**
 * Clears all recent module history for a user.
 *
 * @param userId - The user's ID
 */
export async function clearRecentModules(userId: string): Promise<void> {
  if (!userId) return;
  const db = getNoSqlDb();
  await db.delItem(RECENT_MODULES_KEY, userId);
}
